(function lpbMarketMap() {
  const LEAFLET_VERSION = '1.9.4';
  const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
  const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
  const sectionState = new WeakMap();
  let leafletPromise = null;

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const normalizeUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  const loadLeafletCss = () => {
    if (document.querySelector(`link[href="${LEAFLET_CSS}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS;
    link.crossOrigin = '';
    document.head.appendChild(link);
  };

  const loadLeaflet = () => {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;

    loadLeafletCss();
    leafletPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = LEAFLET_JS;
      script.defer = true;
      script.crossOrigin = '';
      script.onload = () => resolve(window.L);
      script.onerror = () => reject(new Error('Leaflet could not be loaded'));
      document.head.appendChild(script);
    });
    return leafletPromise;
  };

  const getMarkets = (section) => {
    const data = section.querySelector('[data-lpb-market-data]');
    if (!data) return [];

    try {
      return JSON.parse(data.textContent || '[]')
        .map((market, index) => ({
          ...market,
          index,
          lat: Number.parseFloat(String(market.lat).replace(',', '.')),
          lng: Number.parseFloat(String(market.lng).replace(',', '.'))
        }))
        .filter((market) => Number.isFinite(market.lat) && Number.isFinite(market.lng));
    } catch (error) {
      console.warn('[LPB market map] Invalid market data', error);
      return [];
    }
  };

  const shortLabel = (market) => {
    if (market.label) return market.label;
    const firstWord = String(market.day || '').split(/[ ·/&,-]+/).filter(Boolean)[0] || '';
    return firstWord ? `${firstWord.slice(0, 3).toUpperCase()}.` : '';
  };

  const isMobileViewport = () =>
    window.matchMedia('(max-width: 760px), (pointer: coarse)').matches;

  const canHoverPopup = () =>
    window.matchMedia('(hover: hover)').matches || !window.matchMedia('(pointer: coarse)').matches;

  const adjustedPositions = (markets) => {
    const groups = new Map();
    markets.forEach((market) => {
      const key = `${market.lat.toFixed(5)},${market.lng.toFixed(5)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(market);
    });

    groups.forEach((group) => {
      if (group.length === 1) {
        group[0].displayLat = group[0].lat;
        group[0].displayLng = group[0].lng;
        return;
      }

      const radius = 0.00018;
      group.forEach((market, index) => {
        const angle = (Math.PI * 2 * index) / group.length;
        market.displayLat = market.lat + Math.sin(angle) * radius;
        market.displayLng = market.lng + Math.cos(angle) * radius;
      });
    });

    return markets;
  };

  const markerHtml = (market) => {
    const label = shortLabel(market);
    return `
      <span class="lpb-market-map__marker" aria-hidden="true">
        <span class="lpb-market-map__marker-pin"></span>
        ${label ? `<span class="lpb-market-map__marker-label">${escapeHtml(label)}</span>` : ''}
      </span>
    `;
  };

  const popupHtml = (market) => {
    const route = normalizeUrl(market.routeUrl);
    const image = market.image && market.image.url ? market.image : null;
    const cityLine = [market.city, market.postalCode].filter(Boolean).join(' ');
    const addressLine = [market.address, cityLine].filter(Boolean).join(' · ');

    return `
      <article class="lpb-market-map-popup">
        ${
          image
            ? `<div class="lpb-market-map-popup__image"><img src="${escapeHtml(image.url)}" alt="${escapeHtml(image.alt || market.name)}" width="${escapeHtml(image.width || 320)}" height="${escapeHtml(image.height || 240)}" loading="lazy"></div>`
            : ''
        }
        <h3 class="lpb-market-map-popup__title">${escapeHtml(market.name)}</h3>
        ${market.day ? `<p class="lpb-market-map-popup__day">${escapeHtml(market.day)}</p>` : ''}
        ${market.dateNote ? `<p class="lpb-market-map-popup__date">${escapeHtml(market.dateNote)}</p>` : ''}
        ${market.hours ? `<p class="lpb-market-map-popup__meta">${escapeHtml(market.hours)}</p>` : ''}
        ${market.city ? `<p class="lpb-market-map-popup__meta">${escapeHtml(cityLine)}</p>` : ''}
        ${market.address ? `<p class="lpb-market-map-popup__address">${escapeHtml(addressLine)}</p>` : ''}
        ${
          route
            ? `<a class="lpb-market-map-popup__route" href="${escapeHtml(route)}" target="_blank" rel="noopener noreferrer">${escapeHtml(market.routeLabel || "Voir l'itinéraire")}</a>`
            : ''
        }
      </article>
    `;
  };

  const setActive = (section, activeIndex) => {
    section.querySelectorAll('[data-lpb-market-list-item]').forEach((button) => {
      button.setAttribute('aria-current', button.dataset.marketIndex === String(activeIndex) ? 'true' : 'false');
    });

    section.querySelectorAll('.lpb-market-map__marker-icon').forEach((icon) => {
      icon.classList.toggle('is-active', icon.dataset.marketIndex === String(activeIndex));
    });
  };

  const destroy = (section) => {
    const previous = sectionState.get(section);
    if (!previous) return;

    previous.observer?.disconnect();
    previous.resize?.();
    previous.map?.remove();
    sectionState.delete(section);
  };

  const buildMap = async (section) => {
    const mapNode = section.querySelector('[data-lpb-market-canvas]');
    const markets = adjustedPositions(getMarkets(section));
    if (!mapNode || markets.length === 0) return;

    destroy(section);

    const L = await loadLeaflet();
    if (!section.isConnected) return;

    const singleZoom = Number.parseInt(section.dataset.singleZoom || '12', 10);
    const maxFitZoom = Number.parseInt(section.dataset.maxFitZoom || '13', 10);
    const map = L.map(mapNode, {
      zoomControl: true,
      scrollWheelZoom: false,
      doubleClickZoom: true,
      touchZoom: true,
      dragging: true,
      tap: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    const bounds = [];
    const markers = [];
    let pinnedMarketIndex = null;
    let hoverCloseTimer = null;

    const clearHoverClose = () => {
      if (!hoverCloseTimer) return;
      window.clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
    };

    const closeHoveredPopup = (market, marker) => {
      clearHoverClose();
      hoverCloseTimer = window.setTimeout(() => {
        if (pinnedMarketIndex === market.index) return;
        marker.closePopup();
      }, 220);
    };

    markets.forEach((market) => {
      const icon = L.divIcon({
        className: 'lpb-market-map__marker-icon',
        html: markerHtml(market),
        iconSize: [54, 44],
        iconAnchor: [27, 40],
        popupAnchor: [0, -38]
      });
      const marker = L.marker([market.displayLat, market.displayLng], {
        icon,
        keyboard: true,
        title: market.name || market.city || 'Marché'
      }).addTo(map);

      const mobilePopup = isMobileViewport();
      marker.bindPopup(popupHtml(market), {
        maxWidth: mobilePopup ? 210 : 280,
        autoPan: true,
        autoPanPadding: mobilePopup ? [6, 6] : [24, 24],
        keepInView: true
      });

      marker.on('mouseover', () => {
        if (!canHoverPopup()) return;
        if (pinnedMarketIndex !== null && pinnedMarketIndex !== market.index) return;
        clearHoverClose();
        marker.openPopup();
      });
      marker.on('mouseout', () => {
        if (!canHoverPopup()) return;
        if (pinnedMarketIndex === market.index) return;
        closeHoveredPopup(market, marker);
      });
      marker.on('click', () => {
        pinnedMarketIndex = market.index;
        clearHoverClose();
        marker.openPopup();
      });
      marker.on('popupopen', (event) => {
        setActive(section, market.index);

        const popupNode = event.popup.getElement();
        if (!popupNode) return;

        popupNode.addEventListener('mouseenter', clearHoverClose);
        popupNode.addEventListener('mouseleave', () => {
          if (pinnedMarketIndex === market.index) return;
          closeHoveredPopup(market, marker);
        });
      });
      marker.on('popupclose', () => {
        if (pinnedMarketIndex === market.index) pinnedMarketIndex = null;
        setActive(section, null);
      });
      markers[market.index] = marker;
      bounds.push([market.displayLat, market.displayLng]);

      window.requestAnimationFrame(() => {
        const iconNode = marker.getElement();
        if (iconNode) iconNode.dataset.marketIndex = String(market.index);
      });
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], singleZoom);
    } else {
      map.fitBounds(bounds, {
        padding: [34, 34],
        maxZoom: maxFitZoom
      });
    }

    const listHandlers = [];
    section.querySelectorAll('[data-lpb-market-list-item]').forEach((button) => {
      const handler = () => {
        const marker = markers[Number.parseInt(button.dataset.marketIndex || '-1', 10)];
        if (!marker) return;
        pinnedMarketIndex = Number.parseInt(button.dataset.marketIndex || '-1', 10);
        clearHoverClose();
        const targetZoom = isMobileViewport() ? map.getZoom() : Math.max(map.getZoom(), singleZoom);
        map.flyTo(marker.getLatLng(), targetZoom, { duration: 0.35 });
        marker.openPopup();
      };
      button.addEventListener('click', handler);
      listHandlers.push(() => button.removeEventListener('click', handler));
    });

    const resizeHandler = () => map.invalidateSize();
    window.addEventListener('resize', resizeHandler);
    sectionState.set(section, {
      map,
      resize: () => {
        window.removeEventListener('resize', resizeHandler);
        listHandlers.forEach((remove) => remove());
      }
    });
  };

  const init = (section) => {
    if (!section || sectionState.has(section)) return;

    const mapNode = section.querySelector('[data-lpb-market-canvas]');
    if (!mapNode) return;

    if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        buildMap(section);
      }, { rootMargin: '240px 0px' });
      observer.observe(section);
      sectionState.set(section, { observer });
      return;
    }

    buildMap(section);
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-lpb-market-map]').forEach(init);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-lpb-market-map]').forEach(destroy);
  });
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll('[data-lpb-market-map]').forEach((section) => {
      const state = sectionState.get(section);
      state?.map?.invalidateSize();
    });
  });

  initAll();
})();
